import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Calendar, Camera, CheckCircle2, ChevronLeft, Download, Eye, FileCheck, FileWarning, Flag, Info, Key, Loader2, Scale, Sparkles, Upload, Users, X } from "lucide-react";
import { VerificationPanel, VerificationBadge, ConfidenceIndicator } from "@/components/shared/VerificationPanel";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";
import { useEngagementTracker } from "@/hooks/useEngagementTracker";

const DOCUMENT_TYPES = [
  { value: "contract", label: "Contract/Agreement" },
  { value: "nda", label: "Non-Disclosure Agreement" },
  { value: "lease", label: "Lease Agreement" },
  { value: "employment", label: "Employment Contract" },
  { value: "partnership", label: "Partnership Agreement" },
  { value: "merger", label: "Merger/Acquisition" },
  { value: "loan", label: "Loan Agreement" },
  { value: "license", label: "License Agreement" },
  { value: "service", label: "Service Agreement" },
  { value: "other", label: "Other Legal Document" }
];

const INDUSTRIES = [
  { value: "technology", label: "Technology" },
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Financial Services" },
  { value: "real_estate", label: "Real Estate" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail/Consumer" },
  { value: "legal", label: "Legal Services" },
  { value: "entertainment", label: "Entertainment/Media" },
  { value: "energy", label: "Energy" },
  { value: "other", label: "Other" }
];

const ANALYSIS_MODULES = [
  { id: "risk_clauses", name: "Risk Clause Identification", icon: AlertTriangle, desc: "Identifies potentially risky terms" },
  { id: "missing_terms", name: "Missing Standard Terms", icon: FileWarning, desc: "Detects absent expected clauses" },
  { id: "disclosure_check", name: "Disclosure Completeness", icon: Eye, desc: "Analyzes disclosure sections" },
  { id: "obligations", name: "Obligations & Deadlines", icon: Calendar, desc: "Extracts obligations and dates" },
  { id: "unusual_language", name: "Unusual Language Flagging", icon: Flag, desc: "Flags non-standard language" },
  { id: "industry_comparison", name: "Industry Standard Comparison", icon: Scale, desc: "Compares to benchmarks" },
  { id: "party_obligations", name: "Party Obligations Matrix", icon: Users, desc: "Maps party responsibilities" },
  { id: "termination_analysis", name: "Termination & Exit Analysis", icon: X, desc: "Analyzes exit conditions" }
];

const LytixLedgerPage = () => {
  const [activeTab, setActiveTab] = useState('upload'); // upload, camera, results
  const [file, setFile] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [form, setForm] = useState({
    document_type: 'contract',
    industry: '',
    jurisdiction: '',
    analysis_modules: ['risk_clauses', 'missing_terms', 'disclosure_check', 'obligations', 'unusual_language'],
    specific_questions: []
  });
  
  const [report, setReport] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState("ai_generated");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newQuestion, setNewQuestion] = useState('');
  
  const { trackEvent } = useEngagementTracker();
  
  useEffect(() => {
    trackEvent('lytix', 'view');
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [trackEvent, cameraStream]);
  
  const toggleModule = (moduleId) => {
    setForm(f => ({
      ...f,
      analysis_modules: f.analysis_modules.includes(moduleId)
        ? f.analysis_modules.filter(m => m !== moduleId)
        : [...f.analysis_modules, moduleId]
    }));
  };
  
  const addQuestion = () => {
    if (!newQuestion.trim()) return;
    setForm(f => ({ ...f, specific_questions: [...f.specific_questions, newQuestion.trim()] }));
    setNewQuestion('');
  };
  
  const removeQuestion = (idx) => {
    setForm(f => ({ ...f, specific_questions: f.specific_questions.filter((_, i) => i !== idx) }));
  };
  
  const handleFileSelect = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      setFile(droppedFile);
      setError(null);
    }
  };
  
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } } 
      });
      setCameraStream(stream);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setActiveTab('camera');
    } catch (err) {
      setError('Unable to access camera. Please check permissions.');
    }
  };
  
  const captureImage = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/png');
      setCapturedImage(imageData);
      
      // Stop camera
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
        setCameraStream(null);
      }
    }
  };
  
  const retakePhoto = () => {
    setCapturedImage(null);
    startCamera();
  };
  
  const analyzeDocument = async () => {
    if (!file && !capturedImage) {
      setError('Please upload a file or capture an image');
      return;
    }
    
    setLoading(true);
    setError(null);
    setUploadProgress(0);
    
    try {
      let response;
      
      if (capturedImage) {
        // Analyze from camera capture
        const formData = new FormData();
        formData.append('image_data', capturedImage);
        formData.append('document_type', form.document_type);
        formData.append('industry', form.industry);
        formData.append('jurisdiction', form.jurisdiction);
        formData.append('analysis_modules', form.analysis_modules.join(','));
        
        response = await axios.post(`${API}/lytix/analyze-image`, formData);
      } else {
        // Analyze from file upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', form.document_type);
        formData.append('industry', form.industry);
        formData.append('jurisdiction', form.jurisdiction);
        formData.append('analysis_modules', form.analysis_modules.join(','));
        formData.append('specific_questions', form.specific_questions.join('|'));
        
        response = await axios.post(`${API}/lytix/analyze`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        });
      }
      
      setReport(response.data);
      setActiveTab('results');
      trackEvent('lytix', 'report_generated', { document_type: form.document_type });
    } catch (e) {
      setError(e.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'high': return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'medium': return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'low': return 'text-blue-600 bg-blue-50 border-blue-200';
      default: return 'text-slate-600 bg-slate-50 border-slate-200';
    }
  };
  
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high': return 'bg-red-600';
      case 'elevated': return 'bg-orange-500';
      case 'moderate': return 'bg-amber-500';
      default: return 'bg-emerald-500';
    }
  };
  
  const resetAnalysis = () => {
    setReport(null);
    setFile(null);
    setCapturedImage(null);
    setActiveTab('upload');
    setError(null);
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="lytix-ledger-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-6xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <FileCheck className="h-8 w-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Lytix Ledger</h1>
                <Badge className="bg-amber-500">PREMIUM</Badge>
              </div>
              <p className="text-muted-foreground">Document intelligence with multi-model cross-validation</p>
            </div>
          </div>
        </div>
        
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {!report ? (
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Panel */}
            <div className="lg:col-span-2 space-y-6">
              {/* Upload/Camera Tabs */}
              <Card>
                <CardHeader>
                  <div className="flex gap-2">
                    <Button 
                      variant={activeTab === 'upload' ? 'default' : 'outline'} 
                      onClick={() => setActiveTab('upload')}
                      className="gap-2"
                    >
                      <Upload className="h-4 w-4" /> Upload Document
                    </Button>
                    <Button 
                      variant={activeTab === 'camera' ? 'default' : 'outline'} 
                      onClick={startCamera}
                      className="gap-2"
                    >
                      <Camera className="h-4 w-4" /> Camera Capture
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {activeTab === 'upload' && (
                    <div 
                      className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${file ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 hover:border-slate-400'}`}
                      onDrop={handleDrop}
                      onDragOver={(e) => e.preventDefault()}
                    >
                      {file ? (
                        <div className="space-y-3">
                          <FileCheck className="h-12 w-12 text-emerald-600 mx-auto" />
                          <div>
                            <p className="font-medium text-emerald-800">{file.name}</p>
                            <p className="text-sm text-emerald-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                          </div>
                          <Button variant="outline" onClick={() => setFile(null)}>
                            <X className="h-4 w-4 mr-2" /> Remove
                          </Button>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <Upload className="h-12 w-12 text-slate-400 mx-auto" />
                          <div>
                            <p className="text-lg font-medium">Drop your document here</p>
                            <p className="text-sm text-muted-foreground">or click to browse</p>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            Supports PDF, Word (.docx), and images (PNG, JPG) up to 25MB
                          </p>
                          <input 
                            type="file" 
                            ref={fileInputRef}
                            className="hidden" 
                            accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp"
                            onChange={handleFileSelect}
                          />
                          <Button variant="outline" onClick={() => fileInputRef.current?.click()}>
                            Select File
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {activeTab === 'camera' && (
                    <div className="space-y-4">
                      {!capturedImage ? (
                        <>
                          <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                            <video 
                              ref={videoRef} 
                              autoPlay 
                              playsInline 
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 border-4 border-white/30 rounded-lg pointer-events-none" />
                          </div>
                          <div className="flex justify-center gap-4">
                            <Button onClick={captureImage} className="gap-2" size="lg">
                              <Camera className="h-5 w-5" /> Capture Document
                            </Button>
                            <Button variant="outline" onClick={() => {
                              if (cameraStream) {
                                cameraStream.getTracks().forEach(track => track.stop());
                                setCameraStream(null);
                              }
                              setActiveTab('upload');
                            }}>
                              Cancel
                            </Button>
                          </div>
                          <p className="text-xs text-center text-muted-foreground">
                            Position the document within the frame and ensure good lighting
                          </p>
                        </>
                      ) : (
                        <>
                          <div className="relative rounded-lg overflow-hidden">
                            <img src={capturedImage} alt="Captured document" className="w-full" />
                          </div>
                          <div className="flex justify-center gap-4">
                            <Button onClick={retakePhoto} variant="outline" className="gap-2">
                              <Camera className="h-4 w-4" /> Retake
                            </Button>
                            <Button onClick={analyzeDocument} disabled={loading} className="gap-2">
                              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                              Analyze This
                            </Button>
                          </div>
                        </>
                      )}
                      <canvas ref={canvasRef} className="hidden" />
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Document Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Document Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="space-y-1.5">
                      <Label>Document Type</Label>
                      <Select value={form.document_type} onValueChange={v => setForm(f => ({...f, document_type: v}))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DOCUMENT_TYPES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Industry</Label>
                      <Select value={form.industry} onValueChange={v => setForm(f => ({...f, industry: v}))}>
                        <SelectTrigger><SelectValue placeholder="Select industry..." /></SelectTrigger>
                        <SelectContent>
                          {INDUSTRIES.map(i => (
                            <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Jurisdiction</Label>
                      <Input 
                        placeholder="e.g., California, Delaware..."
                        value={form.jurisdiction}
                        onChange={e => setForm(f => ({...f, jurisdiction: e.target.value}))}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Specific Questions */}
              <Card>
                <CardHeader>
                  <CardTitle>Specific Questions (Optional)</CardTitle>
                  <CardDescription>Ask specific questions about the document</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2">
                    <Input 
                      placeholder="e.g., Are there any unusual termination clauses?"
                      value={newQuestion}
                      onChange={e => setNewQuestion(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && addQuestion()}
                    />
                    <Button onClick={addQuestion} variant="outline">Add</Button>
                  </div>
                  {form.specific_questions.length > 0 && (
                    <div className="space-y-2">
                      {form.specific_questions.map((q, i) => (
                        <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{q}</span>
                          <Button variant="ghost" size="sm" onClick={() => removeQuestion(i)}>
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
            
            {/* Sidebar */}
            <div className="space-y-6">
              {/* Analysis Modules */}
              <Card>
                <CardHeader>
                  <CardTitle>Analysis Modules</CardTitle>
                  <CardDescription>Select which analyses to run</CardDescription>
                </CardHeader>
                <CardContent className="space-y-2">
                  {ANALYSIS_MODULES.map(module => (
                    <div 
                      key={module.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        form.analysis_modules.includes(module.id) 
                          ? 'border-amber-500 bg-amber-50' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                      onClick={() => toggleModule(module.id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                          form.analysis_modules.includes(module.id) ? 'bg-amber-500 text-white' : 'bg-slate-100'
                        }`}>
                          <module.icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{module.name}</p>
                          <p className="text-xs text-muted-foreground">{module.desc}</p>
                        </div>
                        {form.analysis_modules.includes(module.id) && (
                          <CheckCircle2 className="h-5 w-5 text-amber-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              {/* Analyze Button */}
              <Card className="bg-gradient-to-br from-amber-600 to-orange-600 text-white border-0">
                <CardContent className="p-6">
                  <Button 
                    className="w-full h-14 text-lg gap-2 bg-white text-amber-700 hover:bg-white/90" 
                    onClick={analyzeDocument}
                    disabled={loading || (!file && !capturedImage)}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        {uploadProgress > 0 ? `Uploading ${uploadProgress}%...` : 'Analyzing...'}
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-5 w-5" />
                        Generate Lytix Report
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-white/80 mt-3 text-center">
                    Multi-model analysis with GPT-4o, Claude & Gemini
                  </p>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-sm text-center">
                      <span className="font-semibold">Implementation Fee:</span> $499.00
                    </p>
                  </div>
                </CardContent>
              </Card>
              
              {/* Info */}
              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Your document is processed securely and analyzed by multiple intelligence engines for comprehensive cross-validation.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in">
            {/* Report Header */}
            <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white border-0">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge className={`${getRiskColor(report.risk_score)} text-white mb-2`}>
                      {report.risk_score?.toUpperCase()} RISK
                    </Badge>
                    <h2 className="text-2xl font-bold mb-1">{report.document_name}</h2>
                    <p className="text-slate-400">
                      {report.document_type} • {report.page_count} pages • {report.word_count?.toLocaleString()} words
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetAnalysis} className="text-white border-white/20 hover:bg-white/10">
                      New Analysis
                    </Button>
                    <Button variant="outline" className="text-white border-white/20 hover:bg-white/10">
                      <Download className="h-4 w-4 mr-2" /> Export PDF
                    </Button>
                  </div>
                </div>
                
                {/* Key Metrics */}
                <div className="grid grid-cols-3 gap-4 mt-6">
                  <div className="p-4 bg-white/10 rounded-lg">
                    <p className="text-slate-400 text-sm">Disclosure Completeness</p>
                    <p className="text-2xl font-bold">{report.disclosure_completeness?.toFixed(0)}%</p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg">
                    <p className="text-slate-400 text-sm">Standard Compliance</p>
                    <p className="text-2xl font-bold">{report.standard_compliance?.toFixed(0)}%</p>
                  </div>
                  <div className="p-4 bg-white/10 rounded-lg">
                    <p className="text-slate-400 text-sm">Cross-Validation Score</p>
                    <p className="text-2xl font-bold">{(report.cross_validation_score * 100).toFixed(0)}%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Models Used */}
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm text-muted-foreground">Analyzed by:</span>
              {report.model_analyses?.map((analysis, i) => (
                <Badge key={i} variant="outline" className="gap-1">
                  <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                  {analysis.provider}/{analysis.model_name}
                </Badge>
              ))}
            </div>
            
            {/* Executive Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Executive Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none whitespace-pre-wrap text-muted-foreground">
                  {report.executive_summary}
                </div>
              </CardContent>
            </Card>
            
            {/* Sections Identified */}
            {report.sections_identified?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Document Sections</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {report.sections_identified.map((section, i) => (
                      <Badge key={i} variant="secondary">{section.name}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Findings */}
            <Card>
              <CardHeader>
                <CardTitle>Analysis Findings</CardTitle>
                <CardDescription>{report.findings?.length || 0} findings identified</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.findings?.map((finding, i) => (
                  <div key={i} className={`p-4 rounded-lg border ${getSeverityColor(finding.severity)}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <Badge variant="outline" className="mb-2">{finding.module?.replace(/_/g, ' ')}</Badge>
                        <h4 className="font-semibold">{finding.title}</h4>
                      </div>
                      <Badge className={`${getSeverityColor(finding.severity)} border`}>
                        {finding.severity?.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-sm mb-3">{finding.description}</p>
                    {finding.original_text && (
                      <div className="p-2 bg-white/50 rounded text-xs font-mono mb-2">
                        "{finding.original_text}"
                      </div>
                    )}
                    {finding.location && (
                      <p className="text-xs text-muted-foreground mb-2">Location: {finding.location}</p>
                    )}
                    {finding.recommendation && (
                      <div className="pt-2 border-t border-current/10">
                        <p className="text-xs font-medium mb-1">Recommendation:</p>
                        <p className="text-xs">{finding.recommendation}</p>
                      </div>
                    )}
                    <div className="mt-2 text-xs text-muted-foreground">
                      Confidence: {(finding.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {/* Legal Disclaimer */}
            <Alert className="border-amber-500 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-xs text-amber-800 whitespace-pre-wrap">
                {report.disclaimer}
              </AlertDescription>
            </Alert>
            
            {/* Lawyer Verification */}
            <div className="mt-6">
              <VerificationPanel
                itemId={report.report_id || report.id || "lytix-report"}
                itemType="report"
                currentStatus={verificationStatus}
                onStatusChange={setVerificationStatus}
              />
            </div>
          </div>
        )}
      </div>
      <Footer />
    </main>
  );
};


export default LytixLedgerPage;
